import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Button,
  Divider,
  useTheme,
} from '@mui/material';
import ForestRoundedIcon from '@mui/icons-material/ForestRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import TimerIcon from '@mui/icons-material/Timer';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import PublicIcon from '@mui/icons-material/Public';
import MapIcon from '@mui/icons-material/Map';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import { useNavigate } from 'react-router-dom';

// Reusable slide-in section wrapper
const SlideInSection = ({ children, direction = 'up', delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.18 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  let transformFrom = 'translateY(40px)';
  if (direction === 'left') transformFrom = 'translateX(-40px)';
  if (direction === 'right') transformFrom = 'translateX(40px)';
  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : transformFrom,
        transition: `opacity 0.9s ${delay}s cubic-bezier(.23,1.01,.32,1), transform 0.9s ${delay}s cubic-bezier(.23,1.01,.32,1)`
      }}
    >
      {children}
    </Box>
  );
};

const sectionSpacing = { pt: { xs: 5, md: 10 }, pb: { xs: 5, md: 10 } };
const containerMaxWidth = 'md';

const ServicesPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Section 1: bg image 1 (intro + veneer)
  const section1 = (
    <Box sx={{ position: 'relative', width: '100%', minHeight: { xs: '70vh', md: '100vh' }, ...sectionSpacing }}>
      <Box
        component="img"
        src="/services_banner/services_banner (1).jpg"
        alt="Section 1 Background"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: 1,
          filter: 'brightness(0.5)',
        }}
      />
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(0,0,0,0.45)', zIndex: 2 }} />
      <Container maxWidth={containerMaxWidth} sx={{ position: 'relative', zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: { xs: '70vh', md: '100vh' }, px: { xs: 1, md: 0 } }}>
        <SlideInSection>
          <Typography variant="h3" sx={{ fontWeight: 900, mb: 2, color: '#fff', letterSpacing: '0.08em', textTransform: 'uppercase', textShadow: '0 4px 24px rgba(0,0,0,0.7)', textAlign: 'center', fontSize: { xs: '2rem', md: '2.5rem' } }}>
            What We Do
          </Typography>
          <Typography variant="h6" sx={{ color: '#f5f3e7', fontWeight: 400, mb: 5, textShadow: '0 2px 8px rgba(0,0,0,0.7)', textAlign: 'center', fontSize: { xs: '1.1rem', md: '1.3rem' } }}>
            Sustainably exporting quality timber from Africa to the world.
          </Typography>
        </SlideInSection>
        <SlideInSection direction="right">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
            <ForestRoundedIcon sx={{ fontSize: { xs: 38, md: 48 }, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, color: '#fff', letterSpacing: '0.04em', textShadow: '0 2px 8px rgba(0,0,0,0.7)', textAlign: 'center', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
              FSC Veneer Trading
            </Typography>
            <Typography variant="body1" sx={{ color: '#f5f3e7', fontWeight: 500, fontSize: { xs: '1rem', md: '1.08rem' }, mb: 1, textShadow: '0 1px 4px rgba(0,0,0,0.7)', textAlign: 'center', maxWidth: 600 }}>
              We source premium veneer from FSC-certified forests in Gabon.
            </Typography>
          </Box>
        </SlideInSection>
      </Container>
    </Box>
  );

  // Section 2: bg image 2 (plywood + zero-stock + logistics)
  const section2 = (
    <Box sx={{ position: 'relative', width: '100%', minHeight: { xs: '70vh', md: '100vh' }, ...sectionSpacing }}>
      <Box
        component="img"
        src="/services_banner/services_banner (2).jpg"
        alt="Section 2 Background"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: 1,
          filter: 'brightness(0.5)',
        }}
      />
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(0,0,0,0.45)', zIndex: 2 }} />
      <Container maxWidth={containerMaxWidth} sx={{ position: 'relative', zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: { xs: '70vh', md: '100vh' }, px: { xs: 1, md: 0 } }}>
        <SlideInSection>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 5 }}>
            <LayersRoundedIcon sx={{ fontSize: { xs: 38, md: 48 }, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, color: '#fff', letterSpacing: '0.04em', textShadow: '0 2px 8px rgba(0,0,0,0.7)', textAlign: 'center', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
              FSC Plywood Export
            </Typography>
            <Typography variant="body1" sx={{ color: '#f5f3e7', fontWeight: 500, fontSize: { xs: '1rem', md: '1.08rem' }, mb: 1, textShadow: '0 1px 4px rgba(0,0,0,0.7)', textAlign: 'center', maxWidth: 600 }}>
              Certified plywood delivered globally.
            </Typography>
          </Box>
        </SlideInSection>
        <SlideInSection>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 5 }}>
            <Inventory2RoundedIcon sx={{ fontSize: { xs: 38, md: 48 }, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, color: '#fff', letterSpacing: '0.04em', textShadow: '0 2px 8px rgba(0,0,0,0.7)', textAlign: 'center', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
              Zero-Stock Model
            </Typography>
            <Typography variant="body1" sx={{ color: '#f5f3e7', fontWeight: 500, fontSize: { xs: '1rem', md: '1.08rem' }, mb: 1, textShadow: '0 1px 4px rgba(0,0,0,0.7)', textAlign: 'center', maxWidth: 600 }}>
              Fast, efficient, and lean operations without stock holding.
            </Typography>
          </Box>
        </SlideInSection>
        <SlideInSection>
          <Box sx={{ width: '100%', mb: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, mb: 4, color: '#fff', letterSpacing: '0.04em', textAlign: 'center', textTransform: 'uppercase', textShadow: '0 2px 8px rgba(0,0,0,0.7)', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
              Custom Global Logistics
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: '0 4px 16px 0 rgba(0,0,0,0.10)', bgcolor: 'rgba(255,255,255,0.93)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', transition: 'box-shadow 0.2s, transform 0.2s', '&:hover': { boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18)', transform: 'scale(1.04)' }, width: '100%', maxWidth: 340, mx: 'auto' }}>
                  <LocalShippingRoundedIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'primary.main', mb: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: 'primary.main', fontSize: { xs: '1rem', md: '1.08rem' } }}>Flexible Shipping</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.97rem', md: '1rem' } }}>Plans for any volume.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: '0 4px 16px 0 rgba(0,0,0,0.10)', bgcolor: 'rgba(255,255,255,0.93)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', transition: 'box-shadow 0.2s, transform 0.2s', '&:hover': { boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18)', transform: 'scale(1.04)' }, width: '100%', maxWidth: 340, mx: 'auto' }}>
                  <PublicIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'primary.main', mb: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: 'primary.main', fontSize: { xs: '1rem', md: '1.08rem' } }}>Global Reach</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.97rem', md: '1rem' } }}>Export to 20+ countries.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: '0 4px 16px 0 rgba(0,0,0,0.10)', bgcolor: 'rgba(255,255,255,0.93)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', transition: 'box-shadow 0.2s, transform 0.2s', '&:hover': { boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18)', transform: 'scale(1.04)' }, width: '100%', maxWidth: 340, mx: 'auto' }}>
                  <MapIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'primary.main', mb: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: 'primary.main', fontSize: { xs: '1rem', md: '1.08rem' } }}>Route Optimization</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.97rem', md: '1rem' } }}>Efficient, timely delivery.</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </SlideInSection>
      </Container>
    </Box>
  );

  // Section 3: bg image 3 (analytics + CTA)
  const section3 = (
    <Box sx={{ position: 'relative', width: '100%', minHeight: { xs: '70vh', md: '100vh' }, pt: { xs: 5, md: 10 }, pb: 0 }}>
      <Box
        component="img"
        src="/services_banner/services_banner (3).jpg"
        alt="Section 3 Background"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: 1,
          filter: 'brightness(0.5)',
        }}
      />
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(0,0,0,0.45)', zIndex: 2 }} />
      <Container maxWidth={containerMaxWidth} sx={{ position: 'relative', zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: { xs: '70vh', md: '100vh' }, px: { xs: 1, md: 0 } }}>
        <SlideInSection>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 5 }}>
            <AnalyticsRoundedIcon sx={{ fontSize: { xs: 40, md: 56 }, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, color: '#fff', letterSpacing: '0.04em', textShadow: '0 2px 8px rgba(0,0,0,0.7)', textAlign: 'center', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
              Strategic Market Insights
            </Typography>
            <Typography variant="body1" sx={{ color: '#f5f3e7', fontWeight: 500, fontSize: { xs: '1rem', md: '1.08rem' }, mb: 1, textShadow: '0 1px 4px rgba(0,0,0,0.7)', textAlign: 'center', maxWidth: 600 }}>
              We offer analytics to help your business scale globally.<br />
              Actionable data for smarter timber trade.
            </Typography>
          </Box>
        </SlideInSection>
        <SlideInSection>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 900, mb: 3, letterSpacing: '0.04em', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
              Want to simplify timber trade with us?
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#fff',
                color: 'primary.main',
                fontWeight: 700,
                px: { xs: 3, md: 5 },
                py: { xs: 1, md: 1.5 },
                borderRadius: 3,
                fontSize: { xs: '1rem', md: '1.15rem' },
                boxShadow: '0 4px 16px 0 rgba(0,0,0,0.13)',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: '#fff',
                },
              }}
              onClick={() => navigate('/contact')}
            >
              Contact Us
            </Button>
          </Box>
        </SlideInSection>
      </Container>
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {section1}
      {section2}
      {section3}
      {/* Remove top margin from Footer to eliminate white bar */}
      <Box sx={{ mt: 0 }}>
        {/* Footer is likely imported in your App.js or Layout, but if not, add it here: */}
        {/* <Footer /> */}
      </Box>
    </Box>
  );
};

export default ServicesPage; 